var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import BarChartIcon from "@mui/icons-material/BarChart";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Card, CardContent, CardHeader, Chip, Grid, Stack, Typography, useMediaQuery, useTheme, } from "@mui/material";
import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRetrieveUserLeagueMatchPermissions, useRetrieveUserLeagueMatchStatsPermissions, } from "../api/PermissionApiProvider";
import { useDeleteLeagueMatch, useRetrieveLeagueMatch, } from "../api/SeasonApiProvider";
import { useRetrievePlayersByTeamId } from "../ApiProvider";
import { UserContext } from "../auth/UserProvider";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import { NotFound } from "../nav/NotFound";
import { findPlayerById } from "../players/utils";
import { findPlayerLeagueMatchStatByPlayerAndTeam } from "../stats/utils";
import { SquadCardWithStats } from "../teams/SquadCardWithStats";
import { DeleteDialog } from "../utils/DeleteDialog";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { ResponsiveActionMenu, } from "../utils/ResponsiveActionMenu";
import { useNumericParams } from "../utils/useNumericParams";
import { MatchDateTimeHeader } from "./MatchDateTimeHeader";
import { MatchScoreboard } from "./MatchScoreboard";
var LeagueMatch = function () {
    var _a;
    var id = useNumericParams().id;
    var user = useContext(UserContext).user;
    var navigate = useNavigate();
    var theme = useTheme();
    var isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    var _b = __read(useState(false), 2), showDelete = _b[0], setShowDelete = _b[1];
    var _c = useRetrieveUserLeagueMatchPermissions({ leagueMatchId: id }), permissions = _c.data, permissionsIsLoading = _c.isLoading, permissionsError = _c.error;
    var _d = permissions || {}, _e = _d.canView, canView = _e === void 0 ? false : _e, _f = _d.canModify, canModify = _f === void 0 ? false : _f;
    var _g = useRetrieveUserLeagueMatchStatsPermissions({
        leagueMatchId: id,
        enabled: !!user,
    }), statsPermissions = _g.data, statsPermissionsIsLoading = _g.isLoading, statsPermissionsError = _g.error;
    var _h = (statsPermissions || {}).canModifyTeams, canModifyTeams = _h === void 0 ? [] : _h;
    var canModifyStats = canModifyTeams.length > 0;
    var _j = useRetrieveLeagueMatch({ id: id, enabled: canView }), leagueMatchIsLoading = _j.isLoading, leagueMatchError = _j.error, leagueMatch = _j.data;
    var deleteMutation = useDeleteLeagueMatch({
        onSuccess: function () {
            navigate("/leagues/".concat(leagueMatch === null || leagueMatch === void 0 ? void 0 : leagueMatch.leagueId, "/seasons/").concat(leagueMatch === null || leagueMatch === void 0 ? void 0 : leagueMatch.seasonId));
        },
    });
    var homeTeam = leagueMatch === null || leagueMatch === void 0 ? void 0 : leagueMatch.homeTeam;
    var awayTeam = leagueMatch === null || leagueMatch === void 0 ? void 0 : leagueMatch.awayTeam;
    var homeTeamId = homeTeam === null || homeTeam === void 0 ? void 0 : homeTeam.id;
    var awayTeamId = awayTeam === null || awayTeam === void 0 ? void 0 : awayTeam.id;
    var _k = useRetrievePlayersByTeamId({ teamId: homeTeamId, enabled: !!user }), homeTeamPlayers = _k.data, homeTeamPlayersError = _k.error, homeTeamPlayersIsLoading = _k.isLoading;
    var _l = useRetrievePlayersByTeamId({ teamId: awayTeamId, enabled: !!user }), awayTeamPlayers = _l.data, awayTeamPlayersError = _l.error, awayTeamPlayersIsLoading = _l.isLoading;
    if (permissionsIsLoading || (user && statsPermissionsIsLoading)) {
        return _jsx(Loading, {});
    }
    if (permissionsError || (user && statsPermissionsError)) {
        return _jsx(ErrorView, { error: permissionsError || statsPermissionsError });
    }
    if (!canView) {
        return _jsx(NotFound, {});
    }
    if (leagueMatchIsLoading ||
        (user && homeTeamId && homeTeamPlayersIsLoading) ||
        (user && awayTeamId && awayTeamPlayersIsLoading)) {
        return _jsx(Loading, {});
    }
    if (id === undefined) {
        return _jsx(NotFound, {});
    }
    if (leagueMatchError ||
        (user && homeTeamPlayersError) ||
        (user && awayTeamPlayersError) ||
        !leagueMatch) {
        return _jsx(ErrorView, {});
    }
    var getHomePlayer = findPlayerById(homeTeamPlayers || []);
    var getAwayPlayer = findPlayerById(awayTeamPlayers || []);
    var getPlayerLeagueMatchStat = findPlayerLeagueMatchStatByPlayerAndTeam((leagueMatch === null || leagueMatch === void 0 ? void 0 : leagueMatch.playerLeagueMatchStats) || []);
    // Get players who played in the match
    var homePlayers = (leagueMatch.playerLeagueMatchStats || [])
        .filter(function (stat) { return stat.teamId === homeTeamId && stat.played !== false; })
        .map(function (stat) { return getHomePlayer(stat.playerId); })
        .filter(function (player) { return player !== undefined; });
    var awayPlayers = (leagueMatch.playerLeagueMatchStats || [])
        .filter(function (stat) { return stat.teamId === awayTeamId && stat.played !== false; })
        .map(function (stat) { return getAwayPlayer(stat.playerId); })
        .filter(function (player) { return player !== undefined; });
    // Format scorers
    var formatMatchScorer = function (playerId, teamId, lastName) {
        var stats = getPlayerLeagueMatchStat(playerId, teamId);
        if (!(stats === null || stats === void 0 ? void 0 : stats.goalsScored))
            return undefined;
        var goalsScored = stats.goalsScored === 1 ? "" : " (".concat(stats.goalsScored, ")");
        return "".concat(lastName).concat(goalsScored);
    };
    var homeScorers = homePlayers
        .map(function (player) {
        var _a, _b, _c;
        return formatMatchScorer(player.id, homeTeamId, (_b = (_a = player === null || player === void 0 ? void 0 : player.externalUser) === null || _a === void 0 ? void 0 : _a.lastName) !== null && _b !== void 0 ? _b : (_c = player === null || player === void 0 ? void 0 : player.user) === null || _c === void 0 ? void 0 : _c.lastName);
    })
        .filter(function (scorer) { return scorer !== undefined; });
    var awayScorers = awayPlayers
        .map(function (player) {
        var _a, _b, _c;
        return formatMatchScorer(player.id, awayTeamId, (_b = (_a = player === null || player === void 0 ? void 0 : player.externalUser) === null || _a === void 0 ? void 0 : _a.lastName) !== null && _b !== void 0 ? _b : (_c = player === null || player === void 0 ? void 0 : player.user) === null || _c === void 0 ? void 0 : _c.lastName);
    })
        .filter(function (scorer) { return scorer !== undefined; });
    var sentOffFor = function (players, teamId) {
        return players
            .filter(function (player) { var _a, _b; return ((_b = (_a = getPlayerLeagueMatchStat(player.id, teamId)) === null || _a === void 0 ? void 0 : _a.redCards) !== null && _b !== void 0 ? _b : 0) > 0; })
            .map(function (player) { var _a, _b, _c; return (_b = (_a = player === null || player === void 0 ? void 0 : player.externalUser) === null || _a === void 0 ? void 0 : _a.lastName) !== null && _b !== void 0 ? _b : (_c = player === null || player === void 0 ? void 0 : player.user) === null || _c === void 0 ? void 0 : _c.lastName; })
            .filter(function (name) { return name !== undefined && name !== null; });
    };
    var homeSentOff = sentOffFor(homePlayers, homeTeamId);
    var awaySentOff = sentOffFor(awayPlayers, awayTeamId);
    var result = leagueMatch.leagueMatchResult;
    var hasResult = result !== null && result !== undefined;
    var getStatusColor = function (status) {
        switch (status) {
            case "completed":
                return "success";
            case "in_progress":
                return "warning";
            case "postponed":
            case "cancelled":
                return "error";
            default:
                return "default";
        }
    };
    var getStatusLabel = function (status) {
        switch (status) {
            case "scheduled":
                return "Scheduled";
            case "in_progress":
                return "In Progress";
            case "completed":
                return "Full Time";
            case "postponed":
                return "Postponed";
            case "cancelled":
                return "Cancelled";
            default:
                return status;
        }
    };
    var actions = __spreadArray(__spreadArray(__spreadArray([], __read((canModify
        ? [
            {
                icon: _jsx(EditIcon, {}),
                label: "Edit Match",
                to: "/leagues/".concat(leagueMatch.leagueId, "/seasons/").concat(leagueMatch.seasonId, "/matches/").concat(id, "/update"),
            },
        ]
        : [])), false), __read((canModifyStats
        ? [
            {
                icon: _jsx(BarChartIcon, {}),
                label: "Edit Stats",
                to: "/leagues/".concat(leagueMatch.leagueId, "/seasons/").concat(leagueMatch.seasonId, "/matches/").concat(id, "/stats/update"),
            },
        ]
        : [])), false), __read((canModify
        ? [
            {
                icon: _jsx(DeleteIcon, {}),
                label: "Delete",
                onClick: function () { return setShowDelete(true); },
            },
        ]
        : [])), false);
    var infoItem = function (text, to) {
        return to && user ? (_jsx(Typography, { component: Link, to: to, variant: "body2", color: "primary.main", sx: {
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
            }, children: text })) : (_jsx(Typography, { variant: "body2", color: "text.secondary", children: text }));
    };
    return (_jsxs(React.Fragment, { children: [_jsx(ErrorSnackbar, { open: deleteMutation.isError, message: "Failed to delete match. Please try again!" }), _jsx(DeleteDialog, { open: showDelete, title: "Delete League Match?", onCancel: function () { return setShowDelete(false); }, onConfirm: function () { return deleteMutation.mutate(id); } }), _jsxs(Card, { sx: { mb: 3 }, children: [_jsx(CardHeader, { title: _jsxs(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", children: [_jsx(Box, { sx: { flex: 1 } }), _jsx(MatchDateTimeHeader, { kickOff: leagueMatch.kickOff, venueName: (_a = leagueMatch.venue) === null || _a === void 0 ? void 0 : _a.name, pitchName: leagueMatch.pitchName, venueLink: user && leagueMatch.venue
                                        ? "/venues/".concat(leagueMatch.venue.id)
                                        : undefined, subtitle: _jsxs(Stack, { direction: "row", spacing: 0.75, alignItems: "center", justifyContent: "center", flexWrap: "wrap", children: [infoItem(leagueMatch.leagueName, "/leagues/".concat(leagueMatch.leagueId)), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "\u00B7" }), infoItem(leagueMatch.seasonName, "/leagues/".concat(leagueMatch.leagueId, "/seasons/").concat(leagueMatch.seasonId)), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "\u00B7" }), infoItem("Round ".concat(leagueMatch.round))] }) }), _jsx(Box, { sx: { flex: 1, display: "flex", justifyContent: "flex-end" }, children: actions.length > 0 && (_jsx(ResponsiveActionMenu, { actions: actions, isMobile: isMobile })) })] }) }), _jsx(CardContent, { children: _jsx(MatchScoreboard, { team1: {
                                name: homeTeam.name,
                                link: user && homeTeamId ? "/teams/".concat(homeTeamId) : undefined,
                            }, team2: {
                                name: awayTeam.name,
                                link: user && awayTeamId ? "/teams/".concat(awayTeamId) : undefined,
                            }, score1: result === null || result === void 0 ? void 0 : result.homeTeamScore, score2: result === null || result === void 0 ? void 0 : result.awayTeamScore, scorers1: homeScorers, scorers2: awayScorers, sentOff1: homeSentOff, sentOff2: awaySentOff, topContent: _jsxs(Stack, { direction: "row", spacing: 1, justifyContent: "center", children: [_jsx(Chip, { label: getStatusLabel(leagueMatch.status), color: getStatusColor(leagueMatch.status), size: "small" }), leagueMatch.matchType !== "regular" && (_jsx(Chip, { label: leagueMatch.matchType === "friendly"
                                            ? "Friendly"
                                            : "Post-Season", size: "small", variant: "outlined", color: leagueMatch.matchType === "friendly" ? "info" : "warning" }))] }) }) })] }), hasResult && (homePlayers.length > 0 || awayPlayers.length > 0) && (_jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, md: 6, children: _jsx(SquadCardWithStats, { team: homeTeam, players: homePlayers, getPlayerStat: function (playerId) {
                                return getPlayerLeagueMatchStat(playerId, homeTeamId);
                            } }) }), _jsx(Grid, { item: true, xs: 12, md: 6, children: _jsx(SquadCardWithStats, { team: awayTeam, players: awayPlayers, getPlayerStat: function (playerId) {
                                return getPlayerLeagueMatchStat(playerId, awayTeamId);
                            } }) })] }))] }));
};
export default LeagueMatch;
